/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkUploadProducts,
  createBulkProductsWithoutDuplicateCheck,
  createProduct,
  deleteProduct,
  deleteBulkProducts,
  getProductsByDatabaseId,
  getProductsByGroupId,
  getProductsByProductId,
  getProductsByHTSCode,
  getProductScreeningsByProductId,
  getProductsBySupplierId,
  getSuppliersByProductId,
  updateProduct,
  updateProductStatus,
  updateProductsStatusFromTo,
} from './products.service';
import { IProduct, PaginationParams } from './products';
import { IQuickFilter } from '../database/database';
import { Time } from '../shared';
import { useInvalidateAllDatabaseKeys } from '../database/database.hooks';
import { STATS_QUERY_KEYS } from '../stats/stats.hooks';
import { auditKeys } from '../audit/audit.hooks';

// Query key constants
const QUERY_KEYS = {
  PRODUCT: 'product',
  PRODUCTS: 'products',
  BY_DATABASE: 'by-database',
  BY_GROUP: 'by-group',
  BY_PRODUCT: 'by-product',
  BY_HTS_CODE: 'by-hts-code',
  BY_SUPPLIER: 'by-supplier',
  SUPPLIERS_BY_PRODUCT: 'suppliers-by-product',
} as const;

const productKeys = {
  all: () => [QUERY_KEYS.PRODUCT] as const,
  products: () => [...productKeys.all(), QUERY_KEYS.PRODUCTS] as const,
  productsByDatabase: (databaseId: string) =>
    [...productKeys.products(), QUERY_KEYS.BY_DATABASE, databaseId] as const,
  productsByGroup: (
    groupId: string,
    options?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      search?: string;
      databaseName?: string;
      exclusions?: string[]; // Fixed typo here
      hierarchy_type?: string;
      hasIssues: boolean;
      quickFilters?: IQuickFilter[];
    },
  ) =>
    [
      ...productKeys.products(),
      QUERY_KEYS.BY_GROUP,
      groupId,
      {
        page: options?.page || 1,
        limit: options?.limit || 10,
        sortBy: options?.sortBy || 'hit_count',
        sortOrder: options?.sortOrder || 'desc',
        search: options?.search || '',
        databaseName: options?.databaseName || '',
        exclusions: options?.exclusions || [], // Fixed typo here
        hierarchy_type: options?.hierarchy_type,
        quickFilters: options?.quickFilters,
      },
    ] as const,
  productsByProduct: (
    productId: string,
    options?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      search?: string;
      hierarchy_type?: string;
      hasIssues?: boolean;
      quickFilters?: IQuickFilter[];
    },
  ) =>
    [
      ...productKeys.products(),
      QUERY_KEYS.BY_PRODUCT,
      productId,
      {
        page: options?.page || 1,
        limit: options?.limit || 10,
        sortBy: options?.sortBy || 'hit_count',
        sortOrder: options?.sortOrder || 'desc',
        search: options?.search || '',
        hierarchy_type: options?.hierarchy_type || '',
        hasIssues: options?.hasIssues || false,
        quickFilters: options?.quickFilters,
      },
    ] as const,

  productsByHTSCode: (
    htsCode: string,
    options?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      search?: string;
      hierarchy_type?: string;
      hasIssues?: boolean;
      quickFilters?: IQuickFilter[];
    },
  ) =>
    [
      ...productKeys.products(),
      QUERY_KEYS.BY_HTS_CODE,
      htsCode,
      {
        page: options?.page || 1,
        limit: options?.limit || 10,
        sortBy: options?.sortBy || 'hit_count',
        sortOrder: options?.sortOrder || 'desc',
        search: options?.search || '',
        hierarchy_type: options?.hierarchy_type || '',
        hasIssues: options?.hasIssues || false,
        quickFilters: options?.quickFilters,
      },
    ] as const,

  productsBySupplier: (supplierId: string) =>
    [...productKeys.products(), QUERY_KEYS.BY_SUPPLIER, supplierId] as const,
  suppliersByProduct: (productId: string) =>
    [...productKeys.all(), QUERY_KEYS.SUPPLIERS_BY_PRODUCT, productId] as const,
};

// Hook to get products by database ID with optional search filtering
export const useGetProductsByDatabaseId = (
  databaseId: string,
  searchTerm = '',
  hierarchyTypeTerm = '',
  paginationParams: PaginationParams,
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'products',
      databaseId,
      paginationParams.page,
      paginationParams.limit,
      paginationParams.sortBy,
      paginationParams.sortOrder,
      searchTerm,
      hierarchyTypeTerm,
      paginationParams.tab,
      paginationParams.hasIssues,
      paginationParams.quickFilters,
    ],
    queryFn: () =>
      getProductsByDatabaseId(databaseId, {
        ...paginationParams,
        search: searchTerm,
        hierarchy_type: hierarchyTypeTerm,
      }),
    staleTime: 0,
    gcTime: 30000,
    enabled: Boolean(databaseId),
  });
  return {
    data: data?.data || [],
    pagination: data?.pagination,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

// Hook to get products by group ID with optional search filtering
export const useGetProductsByGroupId = (
  groupId: string,
  searchTerm = '',
  paginationParams: PaginationParams,
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: productKeys.productsByGroup(groupId, {
      page: paginationParams.page,
      limit: paginationParams.limit,
      sortBy: paginationParams.sortBy,
      sortOrder: paginationParams.sortOrder,
      search: searchTerm,
      databaseName: paginationParams.databaseName,
      exclusions: paginationParams.exclusions, // Fixed typo
      hierarchy_type: paginationParams.hierarchy_type,
      hasIssues: paginationParams.hasIssues ?? false,
      quickFilters: paginationParams.quickFilters,
    }),
    queryFn: () =>
      getProductsByGroupId(groupId, {
        ...paginationParams,
        search: searchTerm,
        hierarchy_type: paginationParams.hierarchy_type,
        hasIssues: paginationParams.hasIssues,
      }),
    staleTime: 0,
    gcTime: 30000,
    enabled: Boolean(groupId),
  });

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

export const useGetProductsByProductId = (
  productId: string,
  searchTerm = '',
  paginationParams: PaginationParams,
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: productKeys.productsByProduct(productId, {
      page: paginationParams.page,
      limit: paginationParams.limit,
      sortBy: paginationParams.sortBy,
      sortOrder: paginationParams.sortOrder,
      search: searchTerm,
      hierarchy_type: paginationParams.hierarchy_type,
      hasIssues: paginationParams.hasIssues ?? false,
    }),
    queryFn: () =>
      getProductsByProductId(productId, {
        ...paginationParams,
        search: searchTerm,
      }),
    staleTime: 0,
    gcTime: 30000,
    enabled: Boolean(productId),
  });

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

export const useGetProductsByHTSCode = (
  htsCode: string,
  searchTerm = '',
  paginationParams: PaginationParams,
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: productKeys.productsByHTSCode(htsCode, {
      page: paginationParams.page,
      limit: paginationParams.limit,
      sortBy: paginationParams.sortBy,
      sortOrder: paginationParams.sortOrder,
      search: searchTerm,
      hierarchy_type: paginationParams.hierarchy_type,
      hasIssues: paginationParams.hasIssues ?? false,
    }),
    queryFn: () =>
      getProductsByHTSCode(htsCode, {
        ...paginationParams,
        search: searchTerm,
      }),
    staleTime: 0,
    gcTime: 30000,
    enabled: Boolean(htsCode),
  });

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

export const useGetProductScreeningsByProductId = (
  productId: string,
  searchTerm = '',
  paginationParams: PaginationParams,
) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: productKeys.productsByProduct(productId, {
      page: paginationParams.page,
      limit: paginationParams.limit,
      sortBy: paginationParams.sortBy,
      sortOrder: paginationParams.sortOrder,
      search: searchTerm,
    }),
    queryFn: () =>
      getProductScreeningsByProductId(productId, {
        ...paginationParams,
        search: searchTerm,
      }),
    staleTime: 0,
    gcTime: 30000,
    enabled: Boolean(productId),
  });

  return {
    data: data?.data || [],
    pagination: data?.pagination,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

// Hook to get suppliers by product ID
export const useGetSuppliersByProductId = (productId?: string) => {
  return useQuery({
    queryKey: productKeys.suppliersByProduct(productId || ''),
    queryFn: () => getSuppliersByProductId(productId || ''),
    staleTime: 5 * Time.MINUTE,
    enabled: Boolean(productId),
  });
};

// Hook to get products by supplier ID
export const useGetProductsBySupplierId = (supplierId?: string) => {
  return useQuery({
    queryKey: productKeys.productsBySupplier(supplierId || ''),
    queryFn: () => getProductsBySupplierId(supplierId || ''),
    staleTime: 5 * Time.MINUTE,
    enabled: Boolean(supplierId),
  });
};

// Mutation hook for creating a product
export const useCreateProduct = () => {
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productData,
      teamId,
    }: {
      productData: any;
      teamId?: string;
    }) => createProduct(productData, teamId),
    onSuccess: (data, variables) => {
      // Use comprehensive invalidation for all database-related keys
      invalidateAllDatabaseKeys(variables.teamId);
      // Clear stats
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });

      // Invalidate audit logs for the new entity
      // Note: createEntity returns boolean, so we can't get the entity ID from response
      // The audit logs will be invalidated by database-level invalidation below

      // Invalidate database-level audit logs if we have database_id
      if (variables.productData?.database_id) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(
            variables.productData.database_id,
          ),
          exact: false,
        });
      }
    },
  });
};

// Mutation hook for updating product
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      productData,
    }: {
      id: string;
      productData: Partial<any>;
    }) => updateProduct(id, productData),
    onSuccess: (data, variables) => {
      // Invalidate and refetch product-related queries
      queryClient.invalidateQueries({ queryKey: productKeys.all() });

      // Invalidate audit logs for this product
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByProduct(variables.id),
        exact: false,
      });

      // Also invalidate database-level audit logs if we have database_id from response
      if (data?.data?.database_id) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(data.data.database_id),
          exact: false,
        });
      }

      // Broad invalidation to ensure audit logs refresh
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error updating product:', error);
    },
  });
};

// Mutation hook for updating product status
export const useUpdateProductStatus = (_databaseId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      statusData,
    }: {
      id: string;
      statusData: { status_flag: string };
    }) => updateProductStatus(id, statusData),
    onSuccess: (data, variables) => {
      // Invalidate and refetch product-related queries
      queryClient.invalidateQueries({ queryKey: productKeys.all() });

      // Invalidate audit logs for this product
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByProduct(variables.id),
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error updating product status:', error);
    },
  });
};

// Mutation hook for deleting a product
export const useDeleteProduct = (databaseId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: (data, variables) => {
      // Invalidate all product queries to refresh data
      queryClient.invalidateQueries({ queryKey: productKeys.all() });
      // Invalidate database queries to update product counts
      queryClient.invalidateQueries({ queryKey: ['database'] });

      // Invalidate audit logs for this product
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogsByProduct(variables),
        exact: false,
      });

      // Also invalidate database-level audit logs if we have databaseId
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(databaseId),
          exact: false,
        });
      }

      // Broad invalidation to ensure audit logs refresh
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error deleting product:', error);
    },
  });
};

// Mutation hook for deleting a product
export const useDeleteBulkProducts = (databaseId?: string) => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: (productIds: string[]) => deleteBulkProducts(productIds),
    onSuccess: () => {
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: ['products', databaseId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      } else {
        invalidateAllDatabaseKeys();
      }

      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook for bulk uploading products
export const useBulkUploadProducts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) => bulkUploadProducts(formData),
    onSuccess: () => {
      // Invalidate all product queries to refresh data
      queryClient.invalidateQueries({ queryKey: productKeys.all() });
      // Invalidate database queries to update product counts
      queryClient.invalidateQueries({ queryKey: ['database'] });

      // Invalidate all product audit logs since we don't know specific IDs for bulk upload
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
    onError: (error) => {
      console.error('Error bulk uploading products:', error);
    },
  });
};

// Mutation hook to create bulk products without duplicate check
export const useCreateBulkProductsWithoutDuplicateCheck = () => {
  const queryClient = useQueryClient();
  const { invalidateAllDatabaseKeys } = useInvalidateAllDatabaseKeys();

  return useMutation({
    mutationFn: ({
      products,
      databaseId,
      createdBy,
      teamId,
    }: {
      products: Partial<IProduct>[];
      databaseId: string;
      createdBy?: string;
      teamId?: string;
    }) => {
      return createBulkProductsWithoutDuplicateCheck(
        products,
        databaseId,
        createdBy,
        teamId,
      );
    },
    onSuccess: (_, variables) => {
      invalidateAllDatabaseKeys(variables.teamId);
      queryClient.invalidateQueries({
        queryKey: [STATS_QUERY_KEYS.stats],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to update entities status from one status to another
export const useUpdateProductsStatusFromTo = (databaseId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      databaseId,
      scope,
      toStatus,
      fromStatus,
      entityIds,
    }: {
      databaseId: string;
      scope: string;
      toStatus: string;
      fromStatus?: string;
      entityIds?: string[];
    }) =>
      updateProductsStatusFromTo(
        databaseId,
        scope,
        toStatus,
        fromStatus,
        entityIds,
      ),
    onSuccess: () => {
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: ['products', databaseId],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [STATS_QUERY_KEYS.stats],
          exact: false,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: productKeys.all() });
      }

      // Invalidate database-level audit logs
      if (databaseId) {
        queryClient.invalidateQueries({
          queryKey: auditKeys.auditLogsByDatabase(databaseId),
          exact: false,
        });
      }

      // Broad invalidation to ensure audit logs refresh
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

export const productTemplateColumns = [
  'model_number',
  'parent',
  'name',
  'description',
  'category',
  'hierarchy_type',
  'quantity',
  'unit_of_measure',
  'manufacturer',
  'vendor',
  'specifications',
  'country_of_origin',
  'hts_code',
  'trans_method',
  'country_of_smelt_pour',
  'entry_date',
  'loading_date',
  'product_value',
  'copper_value',
  'aluminum_value',
  'steel_value',
  'copper_percent',
  'aluminum_percent',
  'steel_percent',
];
